import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { firstValueFrom, Subscription } from 'rxjs';

import { TranscriptionRepository } from '@xmedical/core';
import { RecordingService } from '../../services/recording.service';
import { DeepgramStreamingService } from '../../services/deepgram-streaming.service';
import { TRANSCRIBE_ERROR_HANDLER, TranscribeErrorHandler } from '../../services/error-handler.token';
import {RecordingDialogData, RecordingDialogResult} from '../../models/recording-state.model';

/**
 * Recording component with voice transcription
 * Handles recording, playback, and real-time transcription via Deepgram
 */
@Component({
  selector: 'xmed-recording',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatSnackBarModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatProgressBarModule
],
  providers: [RecordingService, DeepgramStreamingService],
  templateUrl: './recording.component.html',
  styleUrls: ['./recording.component.scss']
})
export class RecordingComponent implements OnInit, OnDestroy {
  private destroyed = false;
  private startCancelled = false;

  // Dialog data - patient/comment context for transcription
  private data = inject<RecordingDialogData>(MAT_DIALOG_DATA, { optional: true });

  // Context signals (from dialog data)
  readonly patientId = signal<number | null>(this.data?.patientId ?? null);
  readonly commentId = signal<number | null>(this.data?.commentId ?? null);

  // Services
  private dialogRef = inject(MatDialogRef<RecordingComponent>);
  private recordingService = inject(RecordingService);
  private snackBar = inject(MatSnackBar);
  private errorHandler: TranscribeErrorHandler = inject(TRANSCRIBE_ERROR_HANDLER, { optional: true })
    ?? { error: (msg, err) => console.error(`[Transcribe] ${msg}`, err) };
  private transcriptionRepo = inject(TranscriptionRepository);
  private deepgramService = inject(DeepgramStreamingService);

  // Recording state
  readonly recordingState = this.recordingService.state$;
  readonly duration = this.recordingService.duration$;
  readonly waveform = this.recordingService.waveform$;
  readonly permissionStatus = this.recordingService.permissionStatus$;

  // Component state
  readonly transcriptionText = signal(''); // Signal for ngModel binding
  readonly audioBlob = signal<Blob | null>(null);
  readonly playbackUrl = signal<string | null>(null);
  readonly transcribing = signal<boolean>(false); // Track upload/save progress
  readonly startingRecording = signal<boolean>(false);

  // Audio playback
  private audioElement?: HTMLAudioElement;
  readonly playing = signal<boolean>(false);
  readonly playbackTime = signal<number>(0);
  readonly playbackDuration = signal<number>(0);
  readonly playbackProgress = signal<number>(0); // 0-100 percentage

  // Deepgram streaming transcription
  private audioChunkSubscription?: Subscription;
  private transcriptionSubscription?: Subscription;

  // S3 upload result (stored for later database record creation)
  private uploadedS3Key?: string;
  private uploadedFileSize?: number;

  // Transcription record result (stored for dialog close)
  private transcriptionRecordId?: number;

  // API key state (from Deepgram service)
  readonly apiKeyReady = this.deepgramService.apiKeyReady;
  readonly apiKeyLoading = this.deepgramService.apiKeyLoading;
  readonly finalizing = this.deepgramService.finalizing;

  // Computed signals
  readonly canRecord = computed(() =>
    this.permissionStatus() === 'granted' &&
    this.recordingState() === 'idle' &&
    this.apiKeyReady() &&
    !this.startingRecording()
  );

  readonly isRecording = computed(() =>
    this.recordingState() === 'recording'
  );

  readonly isPaused = computed(() =>
    this.recordingState() === 'paused'
  );

  readonly isStopped = computed(() =>
    this.recordingState() === 'stopped'
  );

  readonly permissionDenied = computed(() =>
    this.permissionStatus() === 'denied'
  );

  readonly permissionPending = computed(() =>
    this.permissionStatus() === 'pending'
  );

  // Show preparing state when permission granted but API key not ready
  readonly preparingToRecord = computed(() =>
    this.permissionStatus() === 'granted' &&
    this.recordingState() === 'idle' &&
    !this.apiKeyReady()
  );

  readonly canInsertTranscription = computed(() =>
    this.transcriptionText().trim().length > 0
  );

  // Formatted duration
  readonly formattedDuration = computed(() =>
    this.formatDuration(this.duration())
  );

  // Formatted playback time
  readonly formattedPlaybackTime = computed(() => {
    // Show current time when playing, total duration when not playing
    if (this.playing()) {
      return this.formatDuration(this.playbackTime());
    } else {
      return this.formatDuration(this.playbackDuration());
    }
  });

  // Formatted playback duration
  readonly formattedPlaybackDuration = computed(() =>
    this.formatDuration(this.playbackDuration())
  );

  ngOnInit(): void {
    // Request permission on init
    this.requestPermission();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.startCancelled = true;

    // Cleanup
    this.recordingService.destroy();

    // Disconnect from Deepgram
    this.disconnectFromDeepgram();

    // Revoke playback URL
    const url = this.playbackUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Request microphone permission and pre-fetch API key
   */
  async requestPermission(): Promise<void> {
    try {
      await this.recordingService.requestPermission();

      // Pre-fetch API key immediately after permission granted
      if (this.permissionStatus() === 'granted') {
        this.prefetchApiKey();
      }
    } catch (error: unknown) {
      this.errorHandler.error('Permission request failed', error);
      this.snackBar.open('Microphone permission denied', 'Close', { duration: 3000 });
    }
  }

  /**
   * Pre-fetch API key from Deepgram service
   */
  private async prefetchApiKey(): Promise<void> {
    try {
      await this.deepgramService.prefetchApiKey();
    } catch (error: unknown) {
      this.errorHandler.error('Failed to prepare transcription service', error);
      this.snackBar.open('Failed to initialize transcription', 'Close', { duration: 3000 });
    }
  }

  /**
   * Start recording and connect to Deepgram for streaming transcription
   */
  async startRecording(): Promise<void> {
    if (this.startingRecording()) {
      return;
    }

    this.startCancelled = false;
    this.startingRecording.set(true);
    try {
      // Connect to Deepgram first
      await this.connectToDeepgram();
      if (this.startCancelled || this.destroyed) {
        await this.disconnectFromDeepgram();
        return;
      }

      // Start recording
      await this.recordingService.startRecording();
      if (this.startCancelled || this.destroyed) {
        await this.disconnectFromDeepgram();
        return;
      }

      // Subscribe to audio chunks and stream to Deepgram
      this.subscribeToAudioChunks();

      // Subscribe to transcription results from Deepgram
      this.subscribeToTranscriptionResults();
    } catch (error: unknown) {
      if (!this.startCancelled && !this.destroyed) {
        this.errorHandler.error('Failed to start recording', error);
        this.snackBar.open('Failed to start recording', 'Close', { duration: 3000 });
      }

      // Cleanup Deepgram on failure
      await this.disconnectFromDeepgram();
    } finally {
      this.startingRecording.set(false);
    }
  }

  /**
   * Pause recording
   */
  pauseRecording(): void {
    try {
      this.recordingService.pauseRecording();
    } catch (error: unknown) {
      this.errorHandler.error('Failed to pause recording', error);
      this.snackBar.open('Failed to pause recording', 'Close', { duration: 3000 });
    }
  }

  /**
   * Resume recording
   */
  resumeRecording(): void {
    try {
      this.recordingService.resumeRecording();
    } catch (error: unknown) {
      this.errorHandler.error('Failed to resume recording', error);
    }
  }

  /**
   * Stop recording and disconnect from Deepgram
   */
  async stopRecording(): Promise<void> {
    try {
      const result = await this.recordingService.stopRecording();

      // Store audio blob
      this.audioBlob.set(result.audioBlob);

      // Create playback URL
      const url = URL.createObjectURL(result.audioBlob);
      this.playbackUrl.set(url);

      // Set playback duration from recording
      this.playbackDuration.set(result.duration);

      // Disconnect from Deepgram (triggers final transcription results)
      await this.disconnectFromDeepgram();

      // Get the final transcription text from Deepgram service
      const transcriptionText = this.deepgramService.transcriptionText();
      if (transcriptionText) {
        this.transcriptionText.set(transcriptionText);
      }

    } catch (error: unknown) {
      this.errorHandler.error('Failed to stop recording', error);
      this.snackBar.open('Failed to process recording', 'Close', { duration: 3000 });
    }
  }

  /**
   * Handle final transcription result - create database record with uploaded S3 file
   */
  private async handleFinalTranscription(transcriptionText: string): Promise<void> {
    // Set transcribing flag to indicate we're saving to database
    this.transcribing.set(true);

    const duration = this.playbackDuration();

    // Check if we have an uploaded S3 key
    if (!this.uploadedS3Key || !this.uploadedFileSize) {
      this.transcribing.set(false);
      this.snackBar.open('Transcription received, but file not uploaded', 'Close', { duration: 3000 });
      return;
    }

    try {
      // Create transcription record in database
      await this.createTranscriptionRecord(
        this.uploadedS3Key,
        this.uploadedFileSize,
        duration,
        transcriptionText
      );

      this.transcribing.set(false);
      this.snackBar.open('Transcription saved successfully', 'Close', { duration: 2000 });

    } catch (error: unknown) {
      this.errorHandler.error('Failed to save transcription record', error);
      this.transcribing.set(false);
      this.snackBar.open('Failed to save transcription', 'Close', { duration: 5000 });
    }
  }

  /**
   * Upload audio blob to S3
   */
  private async uploadAudioToS3(audioBlob: Blob): Promise<string> {
    try {
      // Generate S3 key
      const now = new Date();
      const dateFolder = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const sessionId = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const s3Key = `transcriptions/${dateFolder}/${sessionId}.webm`;

      // Get signed PUT URL
      const signedUrl = await firstValueFrom(
        this.transcriptionRepo.getSignedUrl(s3Key, 'PUT', 60)
      );

      // Upload to S3
      await firstValueFrom(
        this.transcriptionRepo.uploadFile(
          new File([audioBlob], s3Key, { type: 'audio/ogg;codecs=opus' }),
          signedUrl
        )
      );
      return s3Key;

    } catch (error: unknown) {
      this.errorHandler.error('S3 upload failed', error);
      throw error;
    }
  }

  /**
   * Create transcription record in backend
   * @returns The created transcription record ID
   */
  private async createTranscriptionRecord(
    s3Key: string,
    size: number,
    durationMs: number,
    transcriptionText: string
  ): Promise<number> {
    try {
      // Extract file name and extension from s3Key
      const fileName = s3Key.split('/').pop() || 'recording.webm';
      const extension = fileName.split('.').pop() || 'webm';

      const recordId = await firstValueFrom(
        this.transcriptionRepo.createTranscriptionRecord({
          patientId: this.patientId(),
          commentId: this.commentId(),
          transcriptionText: transcriptionText,
          fileName: fileName,
          extension: extension,
          path: s3Key,
          durationInSeconds: Math.round(durationMs / 1000),
          size: size
        })
      );

      // Store the record ID for later use
      this.transcriptionRecordId = recordId;
      return recordId;

    } catch (error: unknown) {
      this.errorHandler.error('Failed to create transcription record', error);
      throw error;
    }
  }

  /**
   * Play audio
   */
  playAudio(): void {
    const url = this.playbackUrl();
    if (!url) {
      return;
    }

    if (!this.audioElement) {
      this.audioElement = new Audio(url);

      this.audioElement.ontimeupdate = () => {
        if (!this.audioElement) { return; }

        const currentTime = this.audioElement.currentTime * 1000;
        this.playbackTime.set(currentTime);

        // Update progress percentage
        const duration = this.playbackDuration() || (this.audioElement.duration * 1000);
        if (duration > 0 && isFinite(duration)) {
          const progress = (currentTime / duration) * 100;
          this.playbackProgress.set(Math.min(100, Math.max(0, progress)));
        }
      };

      this.audioElement.onloadedmetadata = () => {
        if (!this.audioElement) { return; }

        const duration = this.audioElement.duration * 1000;
        if (isFinite(duration) && duration > 0) {
          this.playbackDuration.set(duration);
        }
      };

      this.audioElement.onended = () => {
        this.playing.set(false);
        // Keep progress bar at 100% and time at duration when playback ends
        this.playbackProgress.set(100);
        this.playbackTime.set(this.playbackDuration());
      };
    }

    this.audioElement.play();
    this.playing.set(true);
  }

  /**
   * Pause audio playback
   */
  pauseAudio(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.playing.set(false);
    }
  }

  /**
   * Delete recording and reset
   */
  deleteRecording(): void {
    // Stop playback and clear handlers
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.ontimeupdate = null;
      this.audioElement.onloadedmetadata = null;
      this.audioElement.onended = null;
      this.audioElement = undefined;
    }

    // Revoke playback URL
    const url = this.playbackUrl();
    if (url) {
      URL.revokeObjectURL(url);
      this.playbackUrl.set(null);
    }

    // Reset state
    this.audioBlob.set(null);
    this.transcriptionText.set('');
    this.transcribing.set(false);
    this.playing.set(false);
    this.playbackTime.set(0);
    this.playbackDuration.set(0);
    this.playbackProgress.set(0);

    // Reset S3 upload tracking
    this.uploadedS3Key = undefined;
    this.uploadedFileSize = undefined;

    // Reset recording service state
    this.recordingService.state$.set('idle');
    this.recordingService.duration$.set(0);
  }

  /**
   * Insert transcription - upload to S3, create record, then close dialog
   * Returns both the text and transcription metadata for linking purposes
   */
  async insertTranscription(): Promise<void> {
    const text = this.transcriptionText().trim();

    if (text.length === 0) {
      this.snackBar.open('No transcription to insert', 'Close', { duration: 2000 });
      return;
    }

    const audioBlob = this.audioBlob();

    // If we have an audio blob, upload it and create the record
    if (audioBlob) {
      this.transcribing.set(true);
      try {
        const s3Key = await this.uploadAudioToS3(audioBlob);
        this.uploadedS3Key = s3Key;
        this.uploadedFileSize = audioBlob.size;

        // Create database record
        await this.handleFinalTranscription(text);

      } catch (uploadError: unknown) {
        this.errorHandler.error('S3 upload failed', uploadError);
        this.transcribing.set(false);
        this.snackBar.open('Upload failed - transcription not saved', 'Close', { duration: 5000 });
        return; // Don't close dialog on failure
      }
    }

    // Return result with transcription metadata for linking
    const result = {
      text,
      transcriptionId: this.transcriptionRecordId ?? null,
      fileName: this.uploadedS3Key ? this.uploadedS3Key.split('/').pop() ?? null : null
    } as RecordingDialogResult;

    this.dialogRef.close(result);
  }

  /**
   * Cancel and close dialog
   */
  async cancel(): Promise<void> {
    this.startCancelled = true;

    // Stop recording if active
    if (this.isRecording() || this.isPaused()) {
      try {
        await this.recordingService.stopRecording();
      } catch {
        // Ignore errors during cleanup
      }
    }

    // Disconnect from Deepgram
    await this.disconnectFromDeepgram();

    // Stop audio playback
    if (this.audioElement) {
      this.audioElement.pause();
    }

    // Revoke playback URL
    const url = this.playbackUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }

    this.dialogRef.close(null);
  }

  /**
   * Format duration in ms to MM:SS
   */
  private formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  // ==================== Deepgram Streaming Transcription Methods ====================

  /**
   * Connect to Deepgram and initialize streaming transcription
   */
  private async connectToDeepgram(): Promise<void> {
    try {
      await this.deepgramService.connect();
    } catch (error: unknown) {
      this.errorHandler.error('Failed to connect to Deepgram', error);
      throw error;
    }
  }

  /**
   * Subscribe to transcription results from Deepgram
   */
  private subscribeToTranscriptionResults(): void {
    this.transcriptionSubscription = this.deepgramService.transcription$.subscribe({
      next: (result) => {
        if (result.isFinal && result.text.trim().length > 0) {
          // Mirror the service's accumulated text (single source of truth)
          this.transcriptionText.set(this.deepgramService.transcriptionText());
        }
      },
      error: (error: unknown) => {
        this.errorHandler.error('Transcription stream error', error);
      }
    });

    // Subscribe to errors
    this.deepgramService.error$.subscribe({
      next: (errorMessage: string) => {
        this.snackBar.open(`Transcription error: ${errorMessage}`, 'Close', { duration: 5000 });
      }
    });
  }

  /**
   * Subscribe to raw audio from RecordingService and stream to Deepgram
   */
  private subscribeToAudioChunks(): void {
    this.audioChunkSubscription = this.recordingService.audioChunk$.subscribe({
      next: (audioData: Blob) => {
        try {
          this.deepgramService.sendAudio(audioData);
        } catch (error: unknown) {
          this.errorHandler.error('Failed to send audio to Deepgram', error);
        }
      },
      error: (error: unknown) => {
        this.errorHandler.error('Audio stream error', error);
      }
    });
  }

  /**
   * Disconnect from Deepgram and cleanup
   * Keeps subscriptions alive during disconnect so finalized results are captured.
   */
  private async disconnectFromDeepgram(): Promise<void> {
    try {
      // Stop sending audio chunks first
      if (this.audioChunkSubscription) {
        this.audioChunkSubscription.unsubscribe();
        this.audioChunkSubscription = undefined;
      }

      // Disconnect from Deepgram — flushes buffer and waits for final results.
      // Transcription subscription stays alive so results during flush are captured.
      await this.deepgramService.disconnect();

      // Now unsubscribe from transcription results after disconnect completes
      if (this.transcriptionSubscription) {
        this.transcriptionSubscription.unsubscribe();
        this.transcriptionSubscription = undefined;
      }

    } catch {
      // Don't rethrow - this is cleanup
    }
  }
}
